<template>
    <aside class="sidebar position--fixed shadow--default scroll">
        <nav class="sidebar__menu position--relative" role="navigation" aria-label="Menu">
            <ul v-if="options.length" class="sidebar__menu__group display--flex">
                <template v-for="( option, index ) in options">
                <li
                    class="sidebar__menu__group__option"
                    :key="option.url">
                    <menu-option
                        :option="option"
                        :isActive="optionActive === index"
                        @click.native.prevent="navigate( option.url )"
                    />
                </li>
                </template>
                <li
                    :class="[
                        'sidebar__menu__bar position--absolute background--green',
                        barClass
                    ]"
                >
                </li>
            </ul>
        </nav>
    </aside>
</template>

<script>
// packages
import { reactive, toRefs, watch } from '@vue/composition-api';

// store
import { useStore } from '@/hooks/use-store';

// plugin
import axios from '@/plugins/axios';

export default {
    components: {
        MenuOption: () => import( /* webpackChunName: 'MenuOption' */ '@/components/bundles/layout/sidebar/menu/MenuOption' )
    },
    props: {
        fetch: Function
    },
    setup ( props, { root }) {
        const { getters } = useStore();
        /**
         * Validação se o utilizador template
         * permissão para ver o ícone no menu.
         *
         * @since 1.0.0
         * @category Method
         * @autor Tiago Martinho
         *
        */
        const buildOption = ( route, icon ) => ({
            ...( getters.can( route, 'see' ) && {
                [route]: {
                    icon: () => import( /* webpackChunkName: '[request]' */ `@/components/bundles/layout/images/icons/Icon${icon}` ),
                    url: route
                }
            })
        });

        /**
         * Construção da lista com as opções
         * de menu.
         *
         * @since 1.0.0
         * @category Method
         * @autor Tiago Martinho
         *
        */
        const buildOptions = () => {
            const list = {
                ...buildOption( 'campaigns', 'Campaign' ),
                ...buildOption( 'reporting', 'Reporting' ),
                ...buildOption( 'users', 'User' ),
                ...buildOption( 'audit', 'Audit' ),
                ...buildOption( 'clients', 'Client' )
            };

            return Object.keys( list ).map( key => list[key] );
        };

        // Associação das rotas com os ícones
        const options = buildOptions();

        const route = root.$route;
        const state = reactive({
            optionActive: 0,
            barClass: '',
            route
        });

        /**
         * [method]
         * Definição da posição da barra na sidebar,
         * conforme a nova rota em que o utilizador se
         * encontra.
         *
         * @since 1.0.0
         * @category Method
         * @autor Tiago Martinho
         *
        */
        const settingBarPosition = () => {
            state.optionActive = options.findIndex( option => [ state.route.name, state.route.meta.parent ].includes( option.url ) );

            // caso não encontre nenhum index,
            // quer dizer que vai para uma página
            // que não esta presente no menu.
            if ( state.optionActive < 0 ) {
                state.barClass = 'js-hide';
                return;
            }

            state.barClass = `js-scale js-index--${state.optionActive}`;

            // é necessário remover o js-scale no final da animação
            // da linha, de forma a no próximo click voltar
            // a executar.
            setTimeout( () => { state.barClass = `js-index--${state.optionActive}`; }, 400 );
        };

        // definição da posição inicial.
        settingBarPosition();

        // watch para actualizar a rota e a classe da barra.
        watch( () => root.$route,
            newRoute => {
                state.route = newRoute;

                // definição da nova posição.
                settingBarPosition();

                // limpa os pedidos pendentes.
                axios.clear();
            });

        /**
         * [method]
         * Implementado o comportamento dos links do menu.
         * Se o utilizador clickar numa nova opção irá mudar de rota,
         * caso clicka na opção em que já encontra é feita um pedido novo
         * a API.
         *
         * @param {String} url
         * @since 1.0.0
         * @category Method
         * @autor Tiago Martinho
         *
        */
        const navigate = url => {
            // se o utilizador carregar no ícone da página
            // actual, é feito o pedido a API e não muda
            // rota.
            if ( state.route.name === url ) {
                props.fetch({});
                return;
            }

            // nova rota
            root.$router.push({ name: url }).catch( () => {});
        };

        return { ...toRefs( state ), options, navigate };
    }
};
</script>

<style lang="scss">
    // =============================================================================
    // Variables
    // =============================================================================
    $coverClass: '.sidebar__menu__bar__cover';

    // index firstPosition $secondPosition
    $sidebar-bar-positions:
        0 67%,
        1 299%,
        2 534%,
        3 767%,
        4 1001%;

    @include keyframes ( height-scale ) {
        0% { min-height: 75px; }
        50% { min-height: 125px; }
        100% { min-height: 75px; }
    }

    // =============================================================================
    // Style
    // =============================================================================
    .sidebar {
        @include transform-position( false, 0, 0, false );
        @include dimensions( 100px, 100vh );
        text-align: center;
        padding-top: $header-height;
        overflow-y: auto;
        overflow-x: hidden;
        &::-webkit-scrollbar {
            width: 2px;  /* for vertical scrollbars */
        }
        &__menu {
            height: 87%;
            &__group {
                flex-direction: column;
                &__option {
                    margin: 50px 0;
                    &__url {
                        align-items: center;
                        justify-content: center;
                        height: 75px;
                        & .path {
                            @include transition( fill .2s ease-in-out );
                        }
                        &--active {
                            & .path {
                                fill: $color-green;
                            }
                        }
                    }
                }
            }
            &__bar {
                @include dimensions( 3px, 75px );
                @include transition( transform .3s ease-in-out );
                &::after {
                    @include pseudo();
                    @include transform-position( 50%, false, false, false, translateY( -50% ) );
                    transform-origin: 50% 50%;
                    @include dimensions( 3px, 75px );
                    background: $color-green;
                }
                &__cover {
                   @include dimensions( inherit, 100% );
                }
                &.js-index {
                    @each $index, $position in $sidebar-bar-positions {
                        &--#{$index} {
                            @include transform( translateY( $position ) );
                        }
                    }
                }
                &.js-scale {
                    &::after {
                        @include animation( height-scale .3s forwards );
                    }
                }
                &.js-hide {
                    opacity: 0;
                }

            }
        }
    }
</style>